import { faEye, faPen, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { CGDeemedDetailsForm } from ".";
import { IDeemedCapitalGains, IFormITR1, IReviewCapitalGains } from "../../../config/interfaces";
import { useAppSelector } from "../../../redux/Store";
import { ITRFilingSelector } from "../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";

const CGDeemedDetails: React.FC = () => {
	const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

	const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);

	const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
	const DeemedCapitalGainsDetails = values.IncomeDetails?.DeemedCapitalGains;

	const hasNonZeroOrNonEmptyValue = (value: string) => {
		return !(value === "" || Number(value) === 0);
	};

	const hasNonZeroOrNonEmptyValues = (deemedData: IDeemedCapitalGains) => {
		return (
			hasNonZeroOrNonEmptyValue(deemedData?.loDeemedTotalAmount) ||
			hasNonZeroOrNonEmptyValue(deemedData?.loUnutilizedCapitalGains) ||
			hasNonZeroOrNonEmptyValue(deemedData?.loOtherDeemedAmount) ||
			hasNonZeroOrNonEmptyValue(deemedData?.shDeemedTotalAmount) ||
			hasNonZeroOrNonEmptyValue(deemedData?.shUnutilizedCapitalGains) ||
			hasNonZeroOrNonEmptyValue(deemedData?.shOtherDeemedAmount)
		);
	};

	const handleTotalCalculation = (deemedData: IDeemedCapitalGains) => {
		const ShortTermGains = Number(deemedData?.shDeemedTotalAmount || "0");
		const LongTermGains = Number(deemedData?.loDeemedTotalAmount || "0");
		const DeemedCapitalGains = ShortTermGains + LongTermGains;

		const CapitalGains: IReviewCapitalGains = {
			...values.CapitalGains,
			TotalDeemedCapitalGain: DeemedCapitalGains
		};

		const TotalCapitalGains = Object.values(CapitalGains)?.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

		const DATA = {
			CapitalGains: CapitalGains,
			TotalCapitalGains: TotalCapitalGains
		};

		setValues({ ...cloneDeep(values), ...DATA });
		setFieldValue("IncomeDetails.DeemedCapitalGains", deemedData);
		setFieldValue("LastSavedTimeStamp", new Date().getTime());
	};

	return (
		<>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>{`Deemed Capital Gains`}</h5>
					<PreventEditComponent>
						{DeemedCapitalGainsDetails ? (
							<Button
								variant="warning"
								size="sm"
								className="text-white"
								onClick={() => setShowAddFormCanvas(() => true)}
							>
								<FASvgIcon icon={faPen} />
								Edit
							</Button>
						) : (
							<Button
								variant="secondary"
								size="sm"
								onClick={() => setShowAddFormCanvas(() => true)}
							>
								<FASvgIcon icon={faPlus} />
								Add
							</Button>
						)}
					</PreventEditComponent>

					{isFilingReadOnly && (
						<Button
							variant="warning"
							size="sm"
							className="text-white"
							onClick={() => setShowAddFormCanvas(() => true)}
						>
							<FASvgIcon icon={faEye} />
							View
						</Button>
					)}
				</div>
				{DeemedCapitalGainsDetails ? (
					<dl className="eds-list eds-dl eds-inline-between">
						<RenderListItem
							title={`Short term capital gains`}
							sum={Number(DeemedCapitalGainsDetails?.shDeemedTotalAmount)}
						/>
						<RenderListItem
							title={`Long term capital gains`}
							sum={Number(DeemedCapitalGainsDetails?.loDeemedTotalAmount)}
						/>
					</dl>
				) : null}
			</section>
			<CGDeemedDetailsForm
				showCanvas={showAddFormCanvas}
				handleCloseBtnPress={(deemedData) => {
					if (deemedData) {
						if (hasNonZeroOrNonEmptyValues(deemedData)) {
							handleTotalCalculation(deemedData);
						} else {
							setFieldValue("IncomeDetails.DeemedCapitalGains", null);
							setFieldValue("LastSavedTimeStamp", new Date().getTime());
						}
					}
					setShowAddFormCanvas(() => false);
				}}
				formValues={DeemedCapitalGainsDetails}
			/>
		</>
	);
};

export default CGDeemedDetails;

const RenderListItem = ({ title, sum = 0 }: { title: string; sum: number }) => {
	return (
		<>
			{sum ? (
				<div className="eds-list--item">
					<dt>{title}</dt>
					<dd>
						<span>
							<PriceDisplay price={sum} />
						</span>
					</dd>
				</div>
			) : null}
		</>
	);
};
